import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Mail, Bell, Send, Edit2, Trash2, Plus, Save, Eye, Copy,
  Users, Store, Truck, Shield, Check, X, Loader2, Search,
  ChevronDown, MoreVertical, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

// Default email templates
const DEFAULT_TEMPLATES = [
  {
    id: 'email_verification',
    name: 'Email Verification',
    subject: 'Verify your email - Hambre',
    category: 'account',
    audience: 'customer',
    description: 'Sent when a user registers to verify their email address',
    variables: ['name', 'code'],
    enabled: true,
  },
  {
    id: 'password_reset',
    name: 'Password Reset',
    subject: 'Reset your password - Hambre',
    category: 'account',
    audience: 'all',
    description: 'Sent when a user requests a password reset',
    variables: ['name', 'reset_link'],
    enabled: true,
  },
  {
    id: 'order_confirmation',
    name: 'Order Confirmation',
    subject: 'Order Confirmed - #{order_id}',
    category: 'orders',
    audience: 'customer',
    description: 'Sent when an order is successfully placed',
    variables: ['order', 'items', 'restaurant_name'],
    enabled: true,
  },
  {
    id: 'order_status_update',
    name: 'Order Status Update',
    subject: 'Order Update - #{order_id}',
    category: 'orders',
    audience: 'customer',
    description: 'Sent when order status changes (confirmed, preparing, delivered)',
    variables: ['order', 'status', 'restaurant_name'],
    enabled: true,
  },
  {
    id: 'delivery_arriving',
    name: 'Delivery Arriving',
    subject: 'Your driver is nearby!',
    category: 'delivery',
    audience: 'customer',
    description: 'Sent when driver is close to delivery address',
    variables: ['order', 'driver_name', 'eta'],
    enabled: true,
  },
  {
    id: 'restaurant_new_order',
    name: 'New Order (Restaurant)',
    subject: 'New Order - #{order_id}',
    category: 'orders',
    audience: 'restaurant',
    description: 'Sent to restaurant when a new order is received',
    variables: ['order', 'items'],
    enabled: true,
  },
  {
    id: 'restaurant_driver_arriving',
    name: 'Driver Arriving (Restaurant)',
    subject: 'Driver arriving for pickup',
    category: 'delivery',
    audience: 'restaurant',
    description: 'Sent to restaurant when driver is arriving',
    variables: ['order', 'driver_name', 'eta'],
    enabled: true,
  },
  {
    id: 'restaurant_payout',
    name: 'Payout Processed (Restaurant)',
    subject: 'Payout of £{amount} processed',
    category: 'payments',
    audience: 'restaurant',
    description: 'Sent when restaurant payout is processed',
    variables: ['amount', 'period', 'orders_count'],
    enabled: true,
  },
  {
    id: 'driver_account_approved',
    name: 'Driver Approved',
    subject: 'Welcome to Hambre - Account Approved!',
    category: 'account',
    audience: 'driver',
    description: 'Sent when driver application is approved',
    variables: ['name'],
    enabled: true,
  },
  {
    id: 'driver_new_delivery',
    name: 'New Delivery Available',
    subject: 'New delivery - £{earnings}',
    category: 'delivery',
    audience: 'driver',
    description: 'Sent when a new delivery is available',
    variables: ['order', 'restaurant_name', 'pickup_address', 'delivery_address', 'earnings'],
    enabled: true,
  },
  {
    id: 'driver_payout',
    name: 'Driver Payout',
    subject: 'Earnings payout - £{amount}',
    category: 'payments',
    audience: 'driver',
    description: 'Sent when driver earnings are paid out',
    variables: ['amount', 'deliveries_count', 'period'],
    enabled: true,
  },
  {
    id: 'admin_new_signup',
    name: 'New Signup Alert',
    subject: 'New {user_type} signup - {name}',
    category: 'alerts',
    audience: 'admin',
    description: 'Sent to admin when a new user signs up',
    variables: ['user_type', 'name', 'email'],
    enabled: true,
  },
  {
    id: 'admin_alert',
    name: 'Admin Alert',
    subject: '[{severity}] {title}',
    category: 'alerts',
    audience: 'admin',
    description: 'Generic alert notification for admins',
    variables: ['alert_type', 'title', 'message', 'severity'],
    enabled: true,
  },
  {
    id: 'waiter_account_created',
    name: 'Waiter Account Created',
    subject: 'Your waiter login code - {restaurant_name}',
    category: 'account',
    audience: 'waiter',
    description: 'Sent when a waiter account is created',
    variables: ['name', 'login_code', 'restaurant_name'],
    enabled: true,
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All Templates', icon: Mail },
  { id: 'account', label: 'Account', icon: Users },
  { id: 'orders', label: 'Orders', icon: Store },
  { id: 'delivery', label: 'Delivery', icon: Truck },
  { id: 'payments', label: 'Payments', icon: Shield },
  { id: 'alerts', label: 'Alerts', icon: Bell },
];

const AUDIENCES = [
  { id: 'all', label: 'All Users' },
  { id: 'customer', label: 'Customers' },
  { id: 'restaurant', label: 'Restaurants' },
  { id: 'driver', label: 'Drivers' },
  { id: 'admin', label: 'Admins' },
  { id: 'waiter', label: 'Waiters' },
];

export default function NotificationManagement() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/email-templates');
      if (response.data?.templates?.length) {
        setTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template) => {
    try {
      setSaving(true);
      await api.put(`/admin/email-templates/${template.id}`, template);
      setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
      setEditingTemplate(null);
      toast.success('Template saved successfully');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = async (templateId, enabled) => {
    try {
      await api.patch(`/admin/email-templates/${templateId}`, { enabled });
      setTemplates(prev => prev.map(t => 
        t.id === templateId ? { ...t, enabled } : t
      ));
      toast.success(`Template ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle template:', error);
      toast.error('Failed to update template');
    }
  };

  const sendTestEmail = async (templateId) => {
    try {
      await api.post(`/admin/email-templates/${templateId}/test`);
      toast.success('Test email sent to your address');
    } catch (error) {
      console.error('Failed to send test:', error);
      toast.error('Failed to send test email');
    }
  };

  const filteredTemplates = templates.filter(t => {
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getAudienceIcon = (audience) => {
    const icons = {
      customer: Users,
      restaurant: Store,
      driver: Truck,
      admin: Shield,
      waiter: Users,
      all: Users,
    };
    return icons[audience] || Users;
  };

  const getAudienceColor = (audience) => {
    const colors = {
      customer: 'bg-blue-100 text-blue-700',
      restaurant: 'bg-orange-100 text-orange-700',
      driver: 'bg-green-100 text-green-700',
      admin: 'bg-purple-100 text-purple-700',
      waiter: 'bg-teal-100 text-teal-700',
      all: 'bg-slate-100 text-slate-700',
    };
    return colors[audience] || colors.all;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Notification Management</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage email templates and notification settings
          </p>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{templates.length}</p>
                <p className="text-sm text-slate-500">Total Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {templates.filter(t => t.enabled).length}
                </p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {templates.filter(t => t.audience === 'customer').length}
                </p>
                <p className="text-sm text-slate-500">Customer Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Bell className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {templates.filter(t => t.category === 'alerts').length}
                </p>
                <p className="text-sm text-slate-500">Alert Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Category Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-2 md:pb-0">
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`whitespace-nowrap ${selectedCategory === cat.id ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                >
                  <cat.icon className="w-4 h-4 mr-2" />
                  {cat.label}
                </Button>
              ))}
            </div>
            
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTemplates.map((template) => {
          const AudienceIcon = getAudienceIcon(template.audience);
          
          return (
            <Card key={template.id} className={`transition-all ${!template.enabled ? 'opacity-60' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getAudienceColor(template.audience)}`}>
                      <AudienceIcon className="w-4 h-4" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getAudienceColor(template.audience)}`}>
                        {AUDIENCES.find(a => a.id === template.audience)?.label || template.audience}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditingTemplate(template)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Template
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setPreviewTemplate(template)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendTestEmail(template.id)}>
                        <Send className="w-4 h-4 mr-2" />
                        Send Test
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => toggleTemplate(template.id, !template.enabled)}
                        className={template.enabled ? 'text-red-600' : 'text-green-600'}
                      >
                        {template.enabled ? (
                          <>
                            <X className="w-4 h-4 mr-2" />
                            Disable
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Enable
                          </>
                        )}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">{template.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    {template.variables?.slice(0, 3).map((v) => (
                      <span key={v} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {v}
                      </span>
                    ))}
                    {template.variables?.length > 3 && (
                      <span className="text-xs text-slate-400">+{template.variables.length - 3}</span>
                    )}
                  </div>
                  <div className={`w-2 h-2 rounded-full ${template.enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredTemplates.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Mail className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No templates found</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Edit Template: {editingTemplate.name}</CardTitle>
              <CardDescription>Modify email template settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={editingTemplate.category}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    {CATEGORIES.filter(c => c.id !== 'all').map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                />
                <p className="text-xs text-slate-500">Use {'{variable}'} for dynamic content</p>
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingTemplate.description}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Audience</Label>
                <select
                  value={editingTemplate.audience}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, audience: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  {AUDIENCES.map((aud) => (
                    <option key={aud.id} value={aud.id}>{aud.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <Label className="mb-2 block">Available Variables</Label>
                <div className="flex flex-wrap gap-2">
                  {editingTemplate.variables?.map((v) => (
                    <code key={v} className="text-sm bg-white border px-2 py-1 rounded">
                      {'{' + v + '}'}
                    </code>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => saveTemplate(editingTemplate)}
                  disabled={saving}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Preview: {previewTemplate.name}</CardTitle>
                <CardDescription>Email template preview with sample data</CardDescription>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setPreviewTemplate(null)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-100 rounded-lg p-4">
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Email Header Preview */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-center">
                    <div className="text-white text-2xl font-bold">Hambre</div>
                    <p className="text-orange-100 text-sm mt-1">Delicious food, delivered fast</p>
                  </div>
                  {/* Email Content Preview */}
                  <div className="p-6">
                    <p className="text-slate-500 text-sm mb-2">Subject: {previewTemplate.subject}</p>
                    <div className="border-t pt-4">
                      <p className="text-slate-700">
                        This is a preview of the <strong>{previewTemplate.name}</strong> template.
                      </p>
                      <p className="text-slate-500 text-sm mt-4">
                        Variables: {previewTemplate.variables?.join(', ')}
                      </p>
                    </div>
                  </div>
                  {/* Email Footer Preview */}
                  <div className="bg-slate-50 p-4 text-center text-sm text-slate-500">
                    <p>© 2026 Hambre Food Delivery. All rights reserved.</p>
                    <p className="mt-1">
                      <a href="#" className="text-orange-500">Privacy Policy</a> · 
                      <a href="#" className="text-orange-500 ml-2">Terms of Service</a>
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => sendTestEmail(previewTemplate.id)}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
                <Button onClick={() => setPreviewTemplate(null)}>
                  Close Preview
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Template Modal */}
      {showAddModal && (
        <AddTemplateModal
          onClose={() => setShowAddModal(false)}
          onSave={async (newTemplate) => {
            try {
              setSaving(true);
              const response = await api.post('/admin/email-templates', newTemplate);
              setTemplates(prev => [...prev, response.data.template || { ...newTemplate, id: Date.now().toString() }]);
              setShowAddModal(false);
              toast.success('Template created successfully');
            } catch (error) {
              console.error('Failed to create template:', error);
              toast.error('Failed to create template');
            } finally {
              setSaving(false);
            }
          }}
          saving={saving}
        />
      )}
    </div>
  );
}

// Add Template Modal Component
function AddTemplateModal({ onClose, onSave, saving }) {
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    subject: '',
    category: 'orders',
    audience: 'customer',
    description: '',
    variables: [],
    enabled: true,
  });
  const [variableInput, setVariableInput] = useState('');

  const addVariable = () => {
    if (variableInput && !newTemplate.variables.includes(variableInput)) {
      setNewTemplate(prev => ({
        ...prev,
        variables: [...prev.variables, variableInput.toLowerCase().replace(/\s+/g, '_')]
      }));
      setVariableInput('');
    }
  };

  const removeVariable = (v) => {
    setNewTemplate(prev => ({
      ...prev,
      variables: prev.variables.filter(variable => variable !== v)
    }));
  };

  const handleSubmit = () => {
    if (!newTemplate.name || !newTemplate.subject) {
      return;
    }
    onSave({
      ...newTemplate,
      id: `custom_${Date.now()}`
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Add New Email Template</CardTitle>
          <CardDescription>Create a custom email template</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Template Name *</Label>
              <Input
                value={newTemplate.name}
                onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                placeholder="e.g., Welcome Email"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <select
                value={newTemplate.category}
                onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                {CATEGORIES.filter(c => c.id !== 'all').map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Subject Line *</Label>
            <Input
              value={newTemplate.subject}
              onChange={(e) => setNewTemplate({ ...newTemplate, subject: e.target.value })}
              placeholder="e.g., Welcome to Hambre, {name}!"
            />
            <p className="text-xs text-slate-500">Use {'{variable}'} for dynamic content</p>
          </div>
          
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={newTemplate.description}
              onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
              rows={2}
              placeholder="Brief description of when this email is sent"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Audience</Label>
            <select
              value={newTemplate.audience}
              onChange={(e) => setNewTemplate({ ...newTemplate, audience: e.target.value })}
              className="w-full px-3 py-2 border rounded-md"
            >
              {AUDIENCES.map((aud) => (
                <option key={aud.id} value={aud.id}>{aud.label}</option>
              ))}
            </select>
          </div>
          
          <div className="space-y-2">
            <Label>Variables</Label>
            <div className="flex gap-2">
              <Input
                value={variableInput}
                onChange={(e) => setVariableInput(e.target.value)}
                placeholder="e.g., customer_name"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addVariable())}
              />
              <Button type="button" onClick={addVariable} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {newTemplate.variables.map((v) => (
                <span key={v} className="text-sm bg-slate-100 text-slate-700 px-2 py-1 rounded flex items-center gap-1">
                  {'{' + v + '}'}
                  <button onClick={() => removeVariable(v)} className="text-slate-400 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={saving || !newTemplate.name || !newTemplate.subject}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Template
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
